import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { fetchBlogEngine, runBlogEngine, startBlogBackfill, fetchBlogBackfillStatus } from "@/lib/dashboardApi";
import { Sparkles, Loader2, Play, ListChecks, AlertTriangle, BarChart3, FileClock, CheckCircle2, XCircle, Rocket } from "lucide-react";

export default function BlogEngine() {
  const { data, isLoading, refetch } = useQuery({ queryKey: ["blog-engine"], queryFn: fetchBlogEngine });
  const [running, setRunning] = useState(false);
  const [backfill, setBackfill] = useState(null);
  const pollRef = useRef(null);

  const loadStatus = async () => {
    try {
      const s = await fetchBlogBackfillStatus();
      setBackfill(s);
      if (s?.state?.running) {
        if (!pollRef.current) pollRef.current = setInterval(loadStatus, 8000);
      } else if (pollRef.current) {
        clearInterval(pollRef.current); pollRef.current = null; refetch();
      }
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadStatus(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  const generate = async () => {
    setRunning(true);
    toast.info("Researching gaps and writing the next article…");
    try {
      const r = await runBlogEngine(true);
      if (r.ok) {
        toast.success("Generating in the background — the new article will appear here in about a minute.");
        setTimeout(() => { refetch(); setRunning(false); }, 60000);
        return;
      }
      toast.error(r.reason || "Engine could not run.");
    } catch (e) { toast.error("Generation failed. Check backend logs."); }
    setRunning(false);
  };

  const runBackfill = async () => {
    const available = data?.queue_total || 0;
    if (!window.confirm(`Backfill will write and publish an article for every open gap (${available} queued now, plus any auto-added from missing questions). Each article uses AI credits. Continue?`)) return;
    try {
      const r = await startBlogBackfill({ publish: true });
      toast.success(`Gap backfill started — writing ${r.count} articles, worst gaps first. You can leave this page; it runs in the background.`);
      setTimeout(loadStatus, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start backfill.");
    }
  };

  if (isLoading || !data) return <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const publishedCount = data.history.filter((h) => h.published).length;
  const bfState = backfill?.state;
  const bfRunning = !!bfState?.running;

  return (
    <div data-testid="content-engine-root">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-navy flex items-center gap-2"><Sparkles className="h-6 w-6 text-launch" /> Content Engine</h1>
          <p className="text-slate-500 mt-1">Daily SEO + GEO blog automation — the <strong>Smart Content Engine</strong> checks Topic Ownership and writes the article that fills your highest-gravity content gap.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={runBackfill} disabled={bfRunning} data-testid="blog-backfill-btn"
            className="inline-flex items-center gap-2 bg-navy hover:bg-navy/90 text-white font-bold px-5 py-2.5 rounded-lg disabled:opacity-60">
            {bfRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />} {bfRunning ? "Backfilling…" : "Backfill All Gaps"}
          </button>
          <button onClick={generate} disabled={running} data-testid="blog-generate-now"
            className="inline-flex items-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold px-5 py-2.5 rounded-lg disabled:opacity-60">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Generate One
          </button>
        </div>
      </div>

      {backfill && (bfRunning || bfState?.started_at) && (
        <div className="mb-6 rounded-xl border border-navy/20 bg-gradient-to-br from-navy/5 to-white p-4" data-testid="be-backfill-status">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-[11px] font-bold uppercase tracking-wide text-navy flex items-center gap-1">
              <Rocket className="h-3.5 w-3.5" /> Gap Backfill {bfRunning ? "in progress" : "complete"}
            </div>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${backfill.max_gap > 15 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
              Mode: {backfill.mode} · biggest gap {backfill.max_gap}%
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-700">
            <span><strong className="text-navy">{bfState.done}</strong>/{bfState.requested} processed</span>
            <span className="text-green-600"><strong>{bfState.published}</strong> published</span>
            {bfState.drafted > 0 && <span className="text-amber-600"><strong>{bfState.drafted}</strong> held as draft (QC)</span>}
            {bfState.requeued > 0 && <span className="text-cool"><strong>{bfState.requeued}</strong> new topics auto-queued</span>}
            <span className="text-slate-400">{backfill.queued_remaining} still queued</span>
          </div>
          {bfState.last && <p className="mt-1 text-xs text-slate-500 truncate">Latest: {bfState.last}</p>}
          {bfRunning && (
            <div className="mt-2 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-navy transition-all" style={{ width: `${Math.min(100, Math.round((bfState.done / Math.max(1, bfState.requested)) * 100))}%` }} />
            </div>
          )}
        </div>
      )}

      {data.next_up && (
        <div className="mb-6 rounded-xl border border-launch/30 bg-gradient-to-br from-launch/5 to-white p-4" data-testid="be-next-up">
          <div className="text-[11px] font-bold uppercase tracking-wide text-launch flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> Next article targets your biggest topic gap</div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="text-navy font-semibold">{data.next_up.title}</span>
            {data.next_up.topic && <span className="rounded-full bg-navy/5 border border-navy/10 px-2 py-0.5 text-[11px] text-navy">Builds: {data.next_up.topic}</span>}
            <span className="rounded-full bg-launch/10 border border-launch/20 px-2 py-0.5 text-[11px] font-semibold text-launch">opportunity {data.next_up.opportunity_score}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[["Topics Queued", data.queue_total], ["Auto-Published", publishedCount], ["Drafts Flagged", data.drafts_flagged.length], ["Cannibalization", data.cannibalization.length]].map(([l, v]) => (
          <div key={l} className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-500">{l}</p>
            <p className="mt-1 text-2xl font-extrabold text-navy">{v}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="be-queue">
          <h3 className="font-bold text-navy flex items-center gap-2 mb-3"><ListChecks className="h-4 w-4 text-cool" /> Next Up (Priority Queue)</h3>
          <ol className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {data.queue.slice(0, 20).map((t) => (
              <li key={t.id} className="py-2.5">
                <p className="font-semibold text-slate-800 text-sm">{t.title}</p>
                <p className="text-xs text-slate-400">{t.pillar} · {t.primary_keyword}{t.location_focus ? ` · ${t.location_focus}` : ""} · P{t.priority}</p>
              </li>
            ))}
          </ol>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="be-coverage">
            <h3 className="font-bold text-navy flex items-center gap-2 mb-3"><BarChart3 className="h-4 w-4 text-cool" /> Topical Coverage Map</h3>
            <ul className="space-y-2">
              {data.coverage.map((c) => (
                <li key={c.pillar} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700">{c.pillar}</span>
                  <span className="text-slate-400">{c.published} live · {c.queued} queued</span>
                </li>
              ))}
            </ul>
          </div>
          {data.cannibalization.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5" data-testid="be-cannibal">
              <h3 className="font-bold text-amber-800 flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4" /> Cannibalization Alerts</h3>
              <ul className="text-sm text-amber-700 space-y-1">
                {data.cannibalization.map((c) => <li key={c.keyword}>"{c.keyword}" targeted by {c.count} pages</li>)}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 mt-6" data-testid="be-history">
        <h3 className="font-bold text-navy flex items-center gap-2 mb-3"><FileClock className="h-4 w-4 text-cool" /> Published Content History</h3>
        {data.history.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">No auto-generated articles yet. Click "Generate Article Now" to create the first one.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                <th className="py-2 pr-3">Title</th><th className="py-2 pr-3">Category</th><th className="py-2 pr-3">Keyword</th><th className="py-2 pr-3">Location</th><th className="py-2 pr-3">Status</th></tr></thead>
              <tbody>
                {data.history.map((h) => (
                  <tr key={h.slug} className="border-b border-slate-50">
                    <td className="py-2 pr-3 font-semibold text-slate-800"><a href={`/blog/${h.slug}`} target="_blank" rel="noreferrer" className="hover:text-launch hover:underline">{h.title}</a></td>
                    <td className="py-2 pr-3 text-slate-500">{h.category}</td>
                    <td className="py-2 pr-3 text-slate-500">{h.primary_keyword}</td>
                    <td className="py-2 pr-3 text-slate-500">{h.location_focus || "—"}</td>
                    <td className="py-2 pr-3">
                      {h.published ? <span className="inline-flex items-center gap-1 text-green-600 font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Live</span>
                        : <span className="inline-flex items-center gap-1 text-amber-600 font-semibold" title={(h.qc_flags || []).join(", ")}><XCircle className="h-3.5 w-3.5" /> Draft</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
